import { isMidasQuoteSource } from '@oneview/utils';
import { quoteMidasApi } from './quoteMidasApi';
import { quoteWatchmanApi } from './quoteWatchmanApi';

// Helper type for API functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiFunction<T> = (...args: any[]) => T;

/**
 * Creates an API selector based on the login type
 * @param midasFn Midas API hook
 * @param watchmanFn Watchman API hook
 */
const createApiSelector = <T>(midasFn: ApiFunction<T>, watchmanFn: ApiFunction<T>): ApiFunction<T> => {
    return isMidasQuoteSource() ? midasFn : watchmanFn;
};

// Watchman-only endpoints
/**
 * @description Account sizes breakdown - Available only in Watchman API
 */
export const useGetQuoteAccountSizesBreakdownQuery = quoteWatchmanApi.useGetQuoteAccountSizesBreakdownQuery;

// Dual API endpoints with automatic selection
export const useGetQuotesQuery = createApiSelector(
    quoteMidasApi.useGetMidasQuotesQuery,
    quoteWatchmanApi.useGetQuotesQuery,
);

export const useGetQuoteQuery = createApiSelector(
    quoteMidasApi.useGetMidasQuoteQuery,
    quoteWatchmanApi.useGetQuoteQuery,
);

export const useGetBondQuoteQuery = createApiSelector(
    quoteMidasApi.useGetMidasBondQuoteQuery,
    quoteWatchmanApi.useGetBondQuoteQuery,
);

export const useLazyGetQuoteQuery = createApiSelector(
    quoteMidasApi.useLazyGetMidasQuoteQuery,
    quoteWatchmanApi.useLazyGetQuoteQuery,
);

export const useGetQuoteHistoricalQuery = createApiSelector(
    quoteMidasApi.useGetMidasQuoteHistoricalQuery,
    quoteWatchmanApi.useGetQuoteHistoricalQuery,
);

/**
 * @description IPO days on market - International markets not supported
 */
export const useGetIpoDaysOnMarketQuery = quoteWatchmanApi.useGetIpoDaysOnMarketQuery;

export const useGetQuoteAnalystsChartQuery = createApiSelector(
    quoteMidasApi.useGetMidasQuoteAnalystsChartQuery,
    quoteWatchmanApi.useGetQuoteAnalystsChartQuery,
);

/**
 * @description Quote shortability - Available only in Watchman API
 */
export const useGetQuoteShortabilityQuery = quoteWatchmanApi.useGetQuoteShortabilityQuery;

/**
 * @description Quote profile - International markets not supported
 */
export const useGetQuoteProfileQuery = quoteWatchmanApi.useGetQuoteProfileQuery;

export const useGetQuoteIntradayQuery = createApiSelector(
    quoteMidasApi.useGetMidasQuoteIntradayQuery,
    quoteWatchmanApi.useGetQuoteIntradayQuery,
);

export const useGetQuoteOptionQuery = createApiSelector(
    quoteMidasApi.useGetMidasOptionQuoteQuery,
    quoteWatchmanApi.useGetQuoteOptionQuery,
);

export const useLazySearchQuotesQuery = createApiSelector(
    quoteMidasApi.useLazySearchMidasQuotesQuery,
    quoteWatchmanApi.useLazySearchQuotesQuery,
);

export const useGetQuoteOptionChainDatesQuery = createApiSelector(
    quoteMidasApi.useGetMidasQuoteOptionChainDatesQuery,
    quoteWatchmanApi.useGetQuoteOptionChainDatesQuery,
);

export const useGetQuoteOptionChainQuotesQuery = createApiSelector(
    quoteMidasApi.useGetMidasOptionChainQuotesQuery,
    quoteWatchmanApi.useGetQuoteOptionChainQuotesQuery,
);

/**
 * @description Top ten stocks - International markets not supported
 */
export const useGetTopTenStocksQuery = quoteWatchmanApi.useGetTopTenStocksQuery;

/**
 * @description Crypto historical data - Available only in Watchman API
 */
export const useGetQuoteCryptoHistoricalQuery = quoteWatchmanApi.useGetQuoteCryptoHistoricalQuery;

/**
 * @description Bond historical data - International markets not supported
 */
export const useGetBondHistoricalQuery = quoteWatchmanApi.useGetBondHistoricalQuery;
export const useLazyGetBondHistoricalQuery = quoteWatchmanApi.useLazyGetBondHistoricalQuery;

export const useGetQuotesFxRateQuery = createApiSelector(
    quoteMidasApi.useGetMidasQuotesFxRateQuery,
    quoteWatchmanApi.useGetQuotesFxRateQuery,
);
